/**
 * Place Type Selector Component
 * Allows users to manually select or confirm their running environment
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { PlaceType, LocationContext } from '@/hooks/usePlaceType';

interface PlaceTypeSelectorProps {
  selectedPlaceType?: PlaceType;
  locationContext?: LocationContext | null;
  onSelect: (placeType: PlaceType) => void;
  onDetectLocation?: () => Promise<LocationContext | void>;
  isDetecting?: boolean;
  compact?: boolean;
}

const PLACE_OPTIONS: { type: PlaceType; icon: string; label: string; description: string }[] = [
  { type: 'beach', icon: '🏖️', label: 'Praia', description: 'Orla, litoral' },
  { type: 'park', icon: '🌳', label: 'Parque', description: 'Praça, área verde' },
  { type: 'urban', icon: '🏙️', label: 'Cidade', description: 'Centro, ruas' },
  { type: 'trail', icon: '🏔️', label: 'Trilha', description: 'Natureza, montanha' },
  { type: 'gym', icon: '🏋️', label: 'Academia', description: 'Indoor, esteira' },
  { type: 'residential', icon: '🏘️', label: 'Bairro', description: 'Residencial' },
  { type: 'stadium', icon: '🏟️', label: 'Pista', description: 'Atletismo' },
];

export function PlaceTypeSelector({
  selectedPlaceType,
  locationContext,
  onSelect,
  onDetectLocation,
  isDetecting = false,
  compact = false,
}: PlaceTypeSelectorProps) {
  const [hoveredType, setHoveredType] = useState<PlaceType | null>(null);

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {PLACE_OPTIONS.map((option) => (
          <motion.button
            key={option.type}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onSelect(option.type)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
              selectedPlaceType === option.type
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-muted hover:bg-muted/80'
            )}
          >
            <span>{option.icon}</span>
            <span>{option.label}</span>
            {selectedPlaceType === option.type && (
              <Check className="h-3 w-3 ml-1" />
            )}
          </motion.button>
        ))}
      </div>
    );
  }

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          Onde você vai correr?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-detect button */}
        {onDetectLocation && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDetectLocation}
            disabled={isDetecting}
            className="w-full"
          >
            {isDetecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Detectando localização...
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4 mr-2" />
                Detectar automaticamente
              </>
            )}
          </Button>
        )}

        {/* Detected location display */}
        {locationContext?.city && (
          <div className="text-sm text-muted-foreground text-center py-2 px-3 bg-muted/50 rounded-lg">
            📍 {locationContext.neighborhood && `${locationContext.neighborhood}, `}
            {locationContext.city}
            {locationContext.state && `, ${locationContext.state}`}
          </div>
        )}

        {/* Place type grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {PLACE_OPTIONS.map((option) => (
            <motion.button
              key={option.type}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onMouseEnter={() => setHoveredType(option.type)}
              onMouseLeave={() => setHoveredType(null)}
              onClick={() => onSelect(option.type)}
              className={cn(
                'relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all',
                'border-2',
                selectedPlaceType === option.type
                  ? 'border-primary bg-primary/10 shadow-lg'
                  : 'border-border/50 bg-background/50 hover:border-primary/50'
              )}
            >
              <span className="text-2xl">{option.icon}</span>
              <span className="text-xs font-medium">{option.label}</span>
              
              {/* Selection indicator */}
              {selectedPlaceType === option.type && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center"
                >
                  <Check className="h-3 w-3 text-primary-foreground" />
                </motion.div>
              )}

              {/* Hover tooltip */}
              {hoveredType === option.type && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg whitespace-nowrap z-10"
                >
                  {option.description}
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Selected place description */}
        {selectedPlaceType && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="text-sm text-center text-muted-foreground"
          >
            {getPlaceTypeDescription(selectedPlaceType)}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}

function getPlaceTypeDescription(placeType: PlaceType): string {
  const descriptions: Record<PlaceType, string> = {
    beach: '🎵 Músicas com vibe tropical e relaxante',
    park: '🎵 Sons da natureza e melodias calmas',
    urban: '🎵 Beats urbanos e eletrônicos energéticos',
    trail: '🎵 Trilhas sonoras épicas e motivacionais',
    gym: '🎵 Alta energia para treino intenso',
    residential: '🎵 Hits populares e músicas conhecidas',
    stadium: '🎵 Hinos de estádio e clássicos do rock',
    unknown: '🎵 Mix variado para sua corrida',
  };
  return descriptions[placeType];
}
